import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CUSTOMER_ITEM_TYPE_OPTIONS,
  ITEM_TYPE_LABELS_FA,
  SERVICE_LABELS_FA,
  type CarpetType,
  type CustomerMatchingPreference,
  type MatchingFlowType,
  type ServiceCatalogKey,
  type ServiceRequestItem,
} from "@ghalishor/contracts";
import type { VoiceSessionState } from "@ghalishor/voice-core";
import { CustomerVoiceExpertV3 } from "./customer-voice-expert-v3";
import { CustomerVoiceExpertV3Client } from "./customer-voice-expert-v3-client";

const AREA_ITEMS = new Set<CarpetType>([
  "machine_made",
  "handmade",
  "silk",
  "kilim",
  "gabbeh",
  "moket",
  "tableau",
  "curtain",
]);
const RECOMMENDED: Readonly<Record<CarpetType, readonly ServiceCatalogKey[]>> =
  {
    machine_made: [
      "washing_standard",
      "washing_premium",
      "stain_removal",
      "repair_edge",
      "repair_hole",
      "protection_nano",
      "cover_pack",
    ],
    handmade: [
      "washing_delicate",
      "dusting",
      "stain_removal",
      "color_bleed_fix",
      "repair_edge",
      "repair_hole",
      "fringe_restore",
      "shirazeh",
      "cover_pack",
    ],
    silk: [
      "washing_silk",
      "dusting",
      "stain_removal",
      "color_fix",
      "fringe_restore",
      "cover_pack",
    ],
    kilim: [
      "washing_kilim",
      "dusting",
      "stain_removal",
      "repair_edge",
      "cover_pack",
    ],
    gabbeh: [
      "washing_gabbeh",
      "dusting",
      "stain_removal",
      "repair_edge",
      "cover_pack",
    ],
    moket: ["washing_moket", "stain_removal", "dry_clean_on_site", "disinfect"],
    tableau: [
      "washing_tableau",
      "dusting",
      "color_fix",
      "repair",
      "cover_pack",
    ],
    sofa: [
      "washing_sofa",
      "stain_removal",
      "dry_clean_on_site",
      "disinfect",
      "protection_nano",
    ],
    curtain: [
      "washing_curtain",
      "stain_removal",
      "dry_clean_on_site",
      "pressing",
    ],
    mattress: [
      "washing_mattress",
      "stain_removal",
      "dry_clean_on_site",
      "disinfect",
    ],
    blanket: ["washing_blanket", "stain_removal", "disinfect", "scent_finish"],
    other: ["washing", "stain_removal", "disinfect"],
  };

const ITEM_VISUALS: Readonly<Record<CarpetType, string>> = {
  machine_made: "/assets/order-services/machine-made-v2.webp",
  handmade: "/assets/order-services/handmade-v2.webp",
  silk: "/assets/order-services/handmade-v2.webp",
  kilim: "/assets/order-services/kilim.webp",
  gabbeh: "/assets/order-services/kilim.webp",
  moket: "/assets/order-services/moquette-v2.webp",
  tableau: "/assets/order-services/handmade-v2.webp",
  sofa: "/assets/order-services/sofa.webp",
  curtain: "/assets/order-services/curtain.webp",
  mattress: "/assets/order-services/mattress.webp",
  blanket: "/assets/order-services/blanket.webp",
  other: "/assets/order-services/blanket.webp",
};

const ITEM_HELP: Readonly<Partial<Record<CarpetType, string>>> = {
  machine_made: "بافت منظم و صنعتی",
  handmade: "دستبافت ظریف و ارزشمند",
  sofa: "شستشو در محل",
  moket: "موکِت ثابت یا تکه‌ای",
  mattress: "تشک و خوشخواب",
  blanket: "پتو و روتختی",
};

type OrderCategoryKey =
  "rug" | "sofa" | "curtain" | "bedding" | "repair" | "other";

const ORDER_CATEGORIES: readonly {
  readonly key: OrderCategoryKey;
  readonly label: string;
  readonly description: string;
  readonly visual: string;
  readonly itemTypes: readonly CarpetType[];
}[] = [
  {
    key: "rug",
    label: "فرش و موکِت",
    description: "فرش ماشینی، دستبافت، گلیم، گبه و موکِت",
    visual: "/assets/order-services/machine-made-v2.webp",
    itemTypes: [
      "machine_made",
      "handmade",
      "silk",
      "kilim",
      "gabbeh",
      "moket",
      "tableau",
    ],
  },
  {
    key: "sofa",
    label: "مبل و صندلی",
    description: "شست‌وشوی مبل، صندلی و کوسن در محل",
    visual: "/assets/order-services/sofa.webp",
    itemTypes: ["sofa"],
  },
  {
    key: "curtain",
    label: "پرده",
    description: "شست‌وشو، بازکردن و نصب دوباره",
    visual: "/assets/order-services/curtain.webp",
    itemTypes: ["curtain"],
  },
  {
    key: "bedding",
    label: "کالای خواب",
    description: "پتو، تشک، خوشخواب و روتختی",
    visual: "/assets/order-services/blanket.webp",
    itemTypes: ["blanket", "mattress"],
  },
  {
    key: "repair",
    label: "رفو و ترمیم",
    description: "پارگی، سوختگی، ریشه و شیرازه",
    visual: "/assets/order-services/handmade-v2.webp",
    itemTypes: ["machine_made", "handmade", "silk", "kilim", "gabbeh"],
  },
  {
    key: "other",
    label: "سایر موارد",
    description: "مواردی که در دسته‌های بالا نیستند",
    visual: "/assets/order-services/mattress.webp",
    itemTypes: ["other"],
  },
];

const MATCHING_PREFERENCE_OPTIONS: readonly {
  readonly value: CustomerMatchingPreference;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: "balanced",
    label: "انتخاب متعادل",
    description: "ترکیب مناسب قیمت، زمان و کیفیت",
  },
  {
    value: "economical",
    label: "قیمت کمتر",
    description: "اولویت با پیشنهادهای اقتصادی‌تر",
  },
  {
    value: "fastest",
    label: "تحویل سریع‌تر",
    description: "اولویت با جمع‌آوری و برگشت سریع",
  },
  {
    value: "quality",
    label: "کیفیت و تضمین",
    description: "اولویت با تخصص، امتیاز و ضمانت",
  },
];

const MATCHING_FLOW_OPTIONS: readonly {
  readonly value: MatchingFlowType;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: "auto_broadcast",
    label: "پیشنهاد هوشمند قالیشور",
    description: "قالیشور بهترین گزینه را براساس اولویت شما پیدا می‌کند.",
  },
  {
    value: "curated_offers",
    label: "انتخاب از بین پیشنهادها",
    description: "چند قالیشویی مناسب را می‌بینید و خودتان انتخاب می‌کنید.",
  },
];

function voiceItemType(label: string): CarpetType {
  const normalized = label.replace(/\s+/g, " ").trim();
  return (
    CUSTOMER_ITEM_TYPE_OPTIONS.find((type) => {
      const itemLabel = ITEM_TYPE_LABELS_FA[type];
      return normalized.includes(itemLabel) || itemLabel.includes(normalized);
    }) ?? "other"
  );
}

function countUnit(type: CarpetType): string {
  if (type === "sofa") return "دست/سرویس";
  if (type === "curtain" || type === "mattress" || type === "other")
    return "عدد";
  return "تخته";
}

export function CustomerMultiRequestWizard(props: {
  readonly isEdit: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly info: string | null;
  readonly requestItems: readonly ServiceRequestItem[];
  readonly notes: string;
  readonly maxReturnWaitDays: string;
  readonly flowType: MatchingFlowType;
  readonly matchingPreference: CustomerMatchingPreference;
  readonly needPickup: boolean;
  readonly needDelivery: boolean;
  readonly floorCount: string;
  readonly hasElevator: boolean;
  readonly onRequestItems: (items: ServiceRequestItem[]) => void;
  readonly onNotes: (v: string) => void;
  readonly onMaxReturnWaitDays: (v: string) => void;
  readonly onFlowType: (v: MatchingFlowType) => void;
  readonly onMatchingPreference: (v: CustomerMatchingPreference) => void;
  readonly onNeedPickup: (v: boolean) => void;
  readonly onNeedDelivery: (v: boolean) => void;
  readonly onFloorCount: (v: string) => void;
  readonly onHasElevator: (v: boolean) => void;
  readonly onSaveDraft: () => void;
  readonly onSubmit: () => void;
  readonly voiceExpert: CustomerVoiceExpertV3Client;
  readonly onAssistantTranscribe: (
    audioBase64: string,
    audioMimeType:
      | "audio/webm"
      | "audio/mp4"
      | "audio/mpeg"
      | "audio/wav"
      | "audio/ogg"
      | "audio/x-m4a",
  ) => Promise<string>;
  readonly onBack: () => void;
  readonly carpetType: CarpetType;
  readonly carpetCount: string;
  readonly approximateSqm: string;
  readonly selectedServices: readonly ServiceCatalogKey[];
  readonly onCarpetType: (v: CarpetType) => void;
  readonly onCarpetCount: (v: string) => void;
  readonly onApproximateSqm: (v: string) => void;
  readonly onToggleService: (key: ServiceCatalogKey) => void;
}) {
  const [editing, setEditing] = useState<{
    type: CarpetType;
    index: number | null;
  } | null>(null);
  const [count, setCount] = useState("1");
  const [sqm, setSqm] = useState("10");
  const [measurementMode, setMeasurementMode] = useState<"provided" | "onsite">(
    "provided",
  );
  const [services, setServices] = useState<ServiceCatalogKey[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [askMore, setAskMore] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [entryMode, setEntryMode] = useState<"voice" | "manual">("voice");
  const [matchingMethodChosen, setMatchingMethodChosen] = useState(false);
  const [manualStage, setManualStage] = useState<1 | 2 | 3>(() =>
    props.requestItems.length > 0 ? 2 : 1,
  );
  const [selectedCategory, setSelectedCategory] =
    useState<OrderCategoryKey | null>(() => {
      const firstItem = props.requestItems[0]?.itemType;
      return firstItem
        ? (ORDER_CATEGORIES.find((category) =>
            category.itemTypes.includes(firstItem),
          )?.key ?? "rug")
        : null;
    });
  const dialogRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const modalOpen = editing !== null || askMore;
  const activeModal = editing !== null ? "editor" : askMore ? "confirm" : null;

  useEffect(() => {
    if (!modalOpen) {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
      return;
    }
    const root = document.documentElement;
    const body = document.body;
    if (
      !returnFocusRef.current &&
      document.activeElement instanceof HTMLElement
    ) {
      returnFocusRef.current = document.activeElement;
    }
    root.classList.add("service-modal-open");
    body.classList.add("service-modal-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setEditing(null);
      setAskMore(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      root.classList.remove("service-modal-open");
      body.classList.remove("service-modal-open");
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!activeModal) return;
    const focusFrame = window.requestAnimationFrame(() =>
      dialogRef.current?.focus(),
    );
    return () => window.cancelAnimationFrame(focusFrame);
  }, [activeModal]);

  const options = useMemo(
    () => (editing ? RECOMMENDED[editing.type] : []),
    [editing],
  );
  const selectedItemTypes = useMemo(
    () => new Set(props.requestItems.map((item) => item.itemType)),
    [props.requestItems],
  );
  const activeCategory = useMemo(
    () =>
      ORDER_CATEGORIES.find((category) => category.key === selectedCategory) ??
      null,
    [selectedCategory],
  );
  const visibleItemTypes = activeCategory?.itemTypes ?? [];
  const defaultServices = (type: CarpetType): readonly ServiceCatalogKey[] => {
    if (selectedCategory !== "repair") return [RECOMMENDED[type][0]!];
    const repairService = RECOMMENDED[type].find(
      (service) =>
        service.includes("repair") ||
        service.includes("fringe") ||
        service === "shirazeh",
    );
    return [repairService ?? RECOMMENDED[type][0]!];
  };
  const open = (type: CarpetType, index: number | null = null) => {
    const current = index === null ? null : (props.requestItems[index] ?? null);
    setEditing({ type, index });
    setCount(String(current?.count ?? 1));
    setSqm(
      String(
        current && AREA_ITEMS.has(type)
          ? current.approximateSqm / current.count
          : AREA_ITEMS.has(type)
            ? 10
            : 1,
      ),
    );
    setMeasurementMode(
      current?.measurementStatus === "onsite" ? "onsite" : "provided",
    );
    setServices(current ? [...current.services] : [...defaultServices(type)]);
    setModalError(null);
  };
  const saveItem = () => {
    if (!editing) return;
    const parsedCount = Number(count);
    const parsedSqm = AREA_ITEMS.has(editing.type)
      ? measurementMode === "onsite"
        ? parsedCount
        : Number(sqm) * parsedCount
      : parsedCount;
    if (
      !Number.isInteger(parsedCount) ||
      parsedCount < 1 ||
      !Number.isFinite(parsedSqm) ||
      parsedSqm <= 0 ||
      services.length === 0
    ) {
      setModalError("تعداد، متراژ و حداقل یک خدمت را کامل کنید.");
      return;
    }
    const next = [...props.requestItems];
    const item: ServiceRequestItem = {
      itemType: editing.type,
      count: parsedCount,
      approximateSqm: parsedSqm,
      measurementStatus: AREA_ITEMS.has(editing.type)
        ? measurementMode
        : "provided",
      services,
    };
    if (editing.index === null) next.push(item);
    else next[editing.index] = item;
    props.onRequestItems(next);
    setEditing(null);
    setAskMore(true);
  };
  const submit = () => {
    if (props.requestItems.length === 0) {
      setModalError("ابتدا حداقل یک خدمت را انتخاب کنید.");
      return;
    }
    props.onSubmit();
  };

  const modalHost = typeof document === "undefined" ? null : document.body;

  const applyVoiceOrder = (state: VoiceSessionState) => {
    const order = state.order;
    const measurementStatus =
      order?.measurementStatus === "onsite" ? "onsite" : "provided";
    if (
      !order?.itemLabel ||
      order.itemCount === null ||
      (measurementStatus !== "onsite" && order.areaPerItemSqm === null)
    )
      return;
    const itemType = voiceItemType(order.itemLabel);
    const approximateSqm =
      measurementStatus === "onsite"
        ? order.itemCount
        : order.itemCount * order.areaPerItemSqm!;
    const duplicate = props.requestItems.some(
      (item) =>
        item.itemType === itemType &&
        item.count === order.itemCount &&
        item.approximateSqm === approximateSqm &&
        item.measurementStatus === measurementStatus,
    );
    if (duplicate) return;
    props.onRequestItems([
      ...props.requestItems,
      {
        itemType,
        count: order.itemCount,
        approximateSqm,
        measurementStatus,
        services: RECOMMENDED[itemType].slice(0, 1),
      },
    ]);
  };

  return (
    <>
      <div
        className="wizard multi-request-wizard easy-order-flow"
        data-order-stage={manualStage}
      >
        {props.error ? (
          <div className="form-errors" role="alert">
            <span>{props.error}</span>
          </div>
        ) : null}
        {props.info ? <div className="success-banner">{props.info}</div> : null}

        <div className="easy-order-progress" aria-label="مراحل ثبت سفارش">
          <span className={manualStage === 1 ? "active" : "completed"}>
            <b>{manualStage > 1 ? "✓" : "۱"}</b> دسته خدمات
          </span>
          <span
            className={
              manualStage === 2 ? "active" : manualStage > 2 ? "completed" : ""
            }
          >
            <b>{manualStage > 2 ? "✓" : "۲"}</b> جزئیات سفارش
          </span>
          <span className={manualStage === 3 ? "active" : ""}>
            <b>۳</b> روش انتخاب
          </span>
        </div>

        {manualStage === 1 ? (
          <>
            <div
              className="order-entry-mode"
              role="tablist"
              aria-label="روش ثبت سفارش"
            >
              <button
                type="button"
                role="tab"
                aria-selected={entryMode === "voice"}
                className={entryMode === "voice" ? "selected" : ""}
                onClick={() => setEntryMode("voice")}
              >
                <span aria-hidden="true">◉</span>
                <strong>با گفت‌وگو</strong>
                <small>سفارش را طبیعی بگویید</small>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={entryMode === "manual"}
                className={entryMode === "manual" ? "selected" : ""}
                onClick={() => setEntryMode("manual")}
              >
                <span aria-hidden="true">☝</span>
                <strong>انتخاب دستی</strong>
                <small>فقط با لمس و تصویر</small>
              </button>
            </div>

            {entryMode === "voice" ? (
              <CustomerVoiceExpertV3
                client={props.voiceExpert}
                screen="new_order"
                authenticated
                orderInput
                busy={props.busy}
                onTranscribe={props.onAssistantTranscribe}
                onOrderState={(state) => {
                  applyVoiceOrder(state);
                  if (state.order?.itemLabel) {
                    const spokenType = voiceItemType(state.order.itemLabel);
                    setSelectedCategory(
                      ORDER_CATEGORIES.find((category) =>
                        category.itemTypes.includes(spokenType),
                      )?.key ?? "other",
                    );
                  }
                  if (state.order?.step === "complete") setManualStage(3);
                }}
              />
            ) : (
              <section
                className="manual-category-stage"
                aria-label="انتخاب دستی دسته خدمات"
              >
                <header className="easy-order-heading">
                  <span className="easy-order-kicker">دسته‌بندی روشن</span>
                  <h2 className="wizard-heading">
                    برای چه چیزی خدمات می‌خواهید؟
                  </h2>
                  <p>
                    هر تصویر یک دستهٔ جداست؛ در مرحلهٔ بعد نوع دقیق را انتخاب
                    می‌کنید.
                  </p>
                </header>
                <div
                  className="service-category-grid"
                  role="radiogroup"
                  aria-label="دسته‌های خدمات"
                >
                  {ORDER_CATEGORIES.map((category) => (
                    <button
                      type="button"
                      role="radio"
                      className={
                        selectedCategory === category.key
                          ? "service-category-card selected"
                          : "service-category-card"
                      }
                      aria-checked={selectedCategory === category.key}
                      key={category.key}
                      onClick={() => setSelectedCategory(category.key)}
                    >
                      <img src={category.visual} alt="" loading="lazy" />
                      <span>
                        <strong>{category.label}</strong>
                        <small>{category.description}</small>
                      </span>
                      <b aria-hidden="true">
                        {selectedCategory === category.key ? "✓" : "○"}
                      </b>
                    </button>
                  ))}
                </div>
                {activeCategory ? (
                  <div className="category-selection-summary" role="status">
                    <span aria-hidden="true">✓</span>
                    <div>
                      <strong>{activeCategory.label} انتخاب شد</strong>
                      <small>{activeCategory.description}</small>
                    </div>
                  </div>
                ) : null}
                <div className="request-submit-dock category-submit-dock">
                  <button
                    type="button"
                    className="cta-primary"
                    disabled={!selectedCategory || props.busy}
                    onClick={() => setManualStage(2)}
                  >
                    {activeCategory
                      ? `ادامه برای ${activeCategory.label}`
                      : "یک دسته را انتخاب کنید"}
                  </button>
                  <small>
                    امکان بازگشت و تغییر دسته در تمام مراحل وجود دارد.
                  </small>
                </div>
              </section>
            )}
          </>
        ) : null}

        {manualStage === 2 ? (
          <>
            <div className="selected-category-strip">
              <img src={activeCategory?.visual} alt="" />
              <span>
                <small>دسته انتخاب‌شده</small>
                <strong>{activeCategory?.label ?? "خدمات قالیشور"}</strong>
              </span>
              <button type="button" onClick={() => setManualStage(1)}>
                تغییر
              </button>
            </div>
            <header className="easy-order-heading">
              <span className="easy-order-kicker">یک انتخاب روشن</span>
              <h2 className="wizard-heading">نوع دقیق مورد را انتخاب کنید</h2>
              <p>
                روی تصویر بزنید؛ تعداد، اندازه و خدمات در یک پنجرهٔ کوتاه تکمیل
                می‌شود.
              </p>
            </header>
            <div className="visual-service-grid multi-item-picker">
              {visibleItemTypes.map((type) => {
                const selected = selectedItemTypes.has(type);
                return (
                  <label
                    className={
                      selected
                        ? "visual-service-card selected"
                        : "visual-service-card"
                    }
                    key={type}
                  >
                    <input
                      className="visual-service-card__control"
                      type="checkbox"
                      checked={selected}
                      onChange={() => open(type)}
                      disabled={props.busy}
                      aria-label={`انتخاب ${ITEM_TYPE_LABELS_FA[type]}`}
                    />
                    <img src={ITEM_VISUALS[type]} alt="" loading="lazy" />
                    <span className="visual-service-card__copy">
                      <strong>{ITEM_TYPE_LABELS_FA[type]}</strong>
                      {ITEM_HELP[type] ? (
                        <small>{ITEM_HELP[type]}</small>
                      ) : null}
                    </span>
                    <span
                      className="visual-service-card__state"
                      aria-hidden="true"
                    >
                      {selected ? "✓" : "+"}
                    </span>
                  </label>
                );
              })}
            </div>

            {props.requestItems.length > 0 ? (
              <section className="request-basket">
                <header>
                  <strong>
                    انتخاب‌های شما (
                    {props.requestItems.length.toLocaleString("fa-IR")})
                  </strong>
                  <span>برای تغییر، روی هر مورد بزنید</span>
                </header>
                {props.requestItems.map((item, index) => (
                  <div
                    className="request-basket__item"
                    key={`${item.itemType}-${index}`}
                  >
                    <img src={ITEM_VISUALS[item.itemType]} alt="" />
                    <button
                      type="button"
                      onClick={() => open(item.itemType, index)}
                    >
                      <strong>{ITEM_TYPE_LABELS_FA[item.itemType]}</strong>
                      <span>
                        {item.count.toLocaleString("fa-IR")}{" "}
                        {countUnit(item.itemType)} ·{" "}
                        {item.services.length.toLocaleString("fa-IR")} خدمت
                        {item.measurementStatus === "onsite"
                          ? " · اندازه‌گیری هنگام تحویل"
                          : AREA_ITEMS.has(item.itemType)
                            ? ` · ${item.approximateSqm.toLocaleString("fa-IR")} متر`
                            : ""}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="danger-link"
                      aria-label={`حذف ${ITEM_TYPE_LABELS_FA[item.itemType]}`}
                      onClick={() =>
                        props.onRequestItems(
                          props.requestItems.filter((_, i) => i !== index),
                        )
                      }
                    >
                      حذف
                    </button>
                  </div>
                ))}
              </section>
            ) : null}

            <section className="easy-order-promise">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>هر مورد جدا و دقیق ثبت می‌شود</strong>
                <small>
                  برای افزودن فرش، موکِت یا کالای دیگر دوباره انتخاب کنید.
                </small>
              </div>
            </section>
            <div className="request-submit-dock">
              <div className="cta-row wizard-actions">
                <button
                  type="button"
                  className="cta-primary"
                  disabled={props.requestItems.length === 0 || props.busy}
                  onClick={() => setManualStage(3)}
                >
                  {props.requestItems.length > 0
                    ? "کامل شد؛ برویم مرحله بعد"
                    : "اول یک مورد انتخاب کنید"}
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setManualStage(1)}
                >
                  بازگشت
                </button>
              </div>
              <small>مرحله بعد: روش انتخاب قالیشویی</small>
            </div>
          </>
        ) : null}

        {manualStage === 3 ? (
          <>
            <header className="easy-order-heading matching-stage-heading">
              <span className="easy-order-kicker">انتخاب نهایی با شماست</span>
              <h2 className="wizard-heading">قالیشویی چگونه انتخاب شود؟</h2>
              <p>
                قالیشور فقط گزینه‌های تأییدشده و مناسب سفارش را بررسی می‌کند.
              </p>
            </header>
            <section
              className="matching-choice-panel"
              aria-labelledby="matching-choice-title"
            >
              <header>
                <span>روش پیدا کردن قالیشویی</span>
                <h3 id="matching-choice-title">یکی از دو روش را انتخاب کنید</h3>
                <p>هیچ گزینه‌ای بدون تأیید شما نهایی نمی‌شود.</p>
              </header>
              {!matchingMethodChosen ? (
                <fieldset className="matching-radio-group matching-method-grid">
                  <legend>روش انتخاب</legend>
                  {MATCHING_FLOW_OPTIONS.map((option) => (
                    <label
                      className="matching-radio-card matching-method-card"
                      key={option.value}
                    >
                      <input
                        type="radio"
                        name="matching-flow"
                        value={option.value}
                        checked={false}
                        onChange={() => {
                          props.onFlowType(option.value);
                          setMatchingMethodChosen(true);
                        }}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        <small>{option.description}</small>
                        <em>
                          {option.value === "auto_broadcast"
                            ? "مناسب برای انتخاب سریع"
                            : "کنترل کامل انتخاب با شماست"}
                        </em>
                      </span>
                      <b aria-hidden="true">‹</b>
                    </label>
                  ))}
                </fieldset>
              ) : (
                <>
                  <div className="matching-selected-method">
                    <span>
                      {MATCHING_FLOW_OPTIONS.find(
                        (option) => option.value === props.flowType,
                      )?.label ?? "روش انتخاب"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMatchingMethodChosen(false)}
                    >
                      تغییر روش
                    </button>
                  </div>
                  <fieldset className="matching-radio-group matching-priority-grid">
                    <legend>مهم‌ترین اولویت شما</legend>
                    {MATCHING_PREFERENCE_OPTIONS.map((option) => (
                      <label
                        className={
                          props.matchingPreference === option.value
                            ? "matching-radio-card selected"
                            : "matching-radio-card"
                        }
                        key={option.value}
                      >
                        <input
                          type="radio"
                          name="matching-preference"
                          value={option.value}
                          checked={props.matchingPreference === option.value}
                          onChange={() =>
                            props.onMatchingPreference(option.value)
                          }
                        />
                        <span>
                          <strong>{option.label}</strong>
                          <small>{option.description}</small>
                        </span>
                        <b aria-hidden="true">
                          {props.matchingPreference === option.value
                            ? "✓"
                            : "○"}
                        </b>
                      </label>
                    ))}
                  </fieldset>
                </>
              )}
            </section>

            {matchingMethodChosen ? (
              <>
                <button
                  type="button"
                  className="advanced-order-toggle"
                  aria-expanded={advancedOpen}
                  onClick={() => setAdvancedOpen((value) => !value)}
                >
                  <span>
                    توضیحات یا تنظیمات بیشتر <small>(اختیاری)</small>
                  </span>
                  <b>{advancedOpen ? "−" : "＋"}</b>
                </button>
                {advancedOpen ? (
                  <section className="advanced-order-panel">
                    <label className="notes-label">
                      توضیحی دارید؟
                      <textarea
                        value={props.notes}
                        onChange={(event) => props.onNotes(event.target.value)}
                        rows={2}
                        placeholder="مثلاً لکه چای دارد یا طبقه سوم هستم"
                      />
                    </label>
                    <div className="metric-row">
                      <label>
                        حداکثر زمان برگشت
                        <input
                          type="number"
                          min={1}
                          max={14}
                          value={props.maxReturnWaitDays}
                          onChange={(event) =>
                            props.onMaxReturnWaitDays(event.target.value)
                          }
                        />
                        <small>روز</small>
                      </label>
                      <label>
                        طبقه
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={props.floorCount}
                          onChange={(event) =>
                            props.onFloorCount(event.target.value)
                          }
                        />
                        <small>همکف = صفر</small>
                      </label>
                    </div>
                    <div className="service-checks">
                      <label
                        className={
                          props.needPickup
                            ? "check-card selected"
                            : "check-card"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={props.needPickup}
                          onChange={(event) =>
                            props.onNeedPickup(event.target.checked)
                          }
                        />
                        <span>جمع‌آوری از منزل</span>
                      </label>
                      <label
                        className={
                          props.needDelivery
                            ? "check-card selected"
                            : "check-card"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={props.needDelivery}
                          onChange={(event) =>
                            props.onNeedDelivery(event.target.checked)
                          }
                        />
                        <span>تحویل در منزل</span>
                      </label>
                      <label
                        className={
                          props.hasElevator
                            ? "check-card selected"
                            : "check-card"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={props.hasElevator}
                          onChange={(event) =>
                            props.onHasElevator(event.target.checked)
                          }
                        />
                        <span>آسانسور دارم</span>
                      </label>
                    </div>
                  </section>
                ) : null}

                <div
                  className="request-submit-dock"
                  role="region"
                  aria-label="عملیات نهایی سفارش"
                >
                  <div className="cta-row wizard-actions">
                    <button
                      type="button"
                      className="cta-primary"
                      disabled={props.busy || !matchingMethodChosen}
                      onClick={submit}
                    >
                      {props.busy ? "در حال ارسال…" : "ثبت و شروع جست‌وجو"}
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setManualStage(2)}
                    >
                      ویرایش سفارش
                    </button>
                  </div>
                  <small>
                    قیمت و زمان پیشنهادها پیش از انتخاب نهایی نمایش داده می‌شود.
                  </small>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>

      {modalHost && editing
        ? createPortal(
            <div
              className="service-modal-backdrop"
              role="presentation"
              onMouseDown={() => setEditing(null)}
            >
              <section
                ref={dialogRef}
                tabIndex={-1}
                className="service-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-modal-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <header>
                  <div>
                    <span>فقط دو سؤال کوتاه</span>
                    <h3 id="service-modal-title">
                      {ITEM_TYPE_LABELS_FA[editing.type]}
                    </h3>
                  </div>
                  <button
                    type="button"
                    aria-label="بستن"
                    onClick={() => setEditing(null)}
                  >
                    ×
                  </button>
                </header>
                <img
                  className="service-modal-visual"
                  src={ITEM_VISUALS[editing.type]}
                  alt=""
                />
                {modalError ? (
                  <div className="form-errors" role="alert">
                    <span>{modalError}</span>
                  </div>
                ) : null}
                <div className="metric-row easy-question-row">
                  <div className="easy-question-field">
                    <div className="easy-question-label">
                      <b>۱</b>
                      <strong>
                        {editing.type === "sofa"
                          ? "چند دست یا سرویس است؟"
                          : editing.type === "blanket"
                            ? "چند تخته دارید؟"
                            : editing.type === "curtain"
                              ? "چند عدد دارید؟"
                              : "چند تخته دارید؟"}
                      </strong>
                    </div>
                    <div className="quantity-stepper">
                      <button
                        type="button"
                        aria-label="کاهش تعداد"
                        onClick={() =>
                          setCount(
                            String(Math.max(1, (Number(count) || 1) - 1)),
                          )
                        }
                      >
                        −
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        aria-label="تعداد"
                        value={count}
                        onChange={(event) => setCount(event.target.value)}
                      />
                      <button
                        type="button"
                        aria-label="افزایش تعداد"
                        onClick={() =>
                          setCount(
                            String(Math.min(20, (Number(count) || 1) + 1)),
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {AREA_ITEMS.has(editing.type) ? (
                    <div className="easy-question-field">
                      <div className="easy-question-label">
                        <b>۲</b>
                        <strong>متراژ تقریبی هر مورد چقدر است؟</strong>
                      </div>
                      <div
                        className="size-shortcuts"
                        aria-label="متراژهای رایج"
                      >
                        {[6, 9, 12].map((size) => (
                          <button
                            type="button"
                            className={
                              measurementMode === "provided" &&
                              Number(sqm) === size
                                ? "selected"
                                : ""
                            }
                            aria-pressed={
                              measurementMode === "provided" &&
                              Number(sqm) === size
                            }
                            onClick={() => {
                              setMeasurementMode("provided");
                              setSqm(String(size));
                            }}
                            key={size}
                          >
                            {size.toLocaleString("fa-IR")} متر
                          </button>
                        ))}
                      </div>
                      <label className="custom-size-field">
                        متراژ دیگر
                        <input
                          type="number"
                          inputMode="decimal"
                          min={0.01}
                          step={0.01}
                          disabled={measurementMode === "onsite"}
                          value={sqm}
                          onChange={(event) => {
                            setMeasurementMode("provided");
                            setSqm(event.target.value);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className={
                          measurementMode === "onsite"
                            ? "onsite-measurement selected"
                            : "onsite-measurement"
                        }
                        aria-pressed={measurementMode === "onsite"}
                        onClick={() => setMeasurementMode("onsite")}
                      >
                        {measurementMode === "onsite" ? "✓ " : ""}
                        متراژ را نمی‌دانم؛ هنگام تحویل اندازه‌گیری شود
                      </button>
                    </div>
                  ) : null}
                </div>
                <strong className="easy-service-title">
                  چه کاری انجام شود؟ <small>پیشنهاد ما انتخاب شده است</small>
                </strong>
                <div className="service-checks">
                  {options.map((service) => (
                    <label
                      className={
                        services.includes(service)
                          ? "check-card selected"
                          : "check-card"
                      }
                      key={service}
                    >
                      <input
                        type="checkbox"
                        checked={services.includes(service)}
                        onChange={() =>
                          setServices((current) =>
                            current.includes(service)
                              ? current.filter((key) => key !== service)
                              : [...current, service],
                          )
                        }
                      />
                      <span>{SERVICE_LABELS_FA[service]}</span>
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  className="cta-primary"
                  onClick={saveItem}
                >
                  تأیید و اضافه‌کردن
                </button>
              </section>
            </div>,
            modalHost,
          )
        : null}

      {modalHost && askMore
        ? createPortal(
            <div className="service-modal-backdrop">
              <section
                ref={dialogRef}
                tabIndex={-1}
                className="service-modal service-modal--confirm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-more-title"
              >
                <div className="confirm-icon">✓</div>
                <h3 id="service-more-title">عالی، اضافه شد</h3>
                <p>
                  مورد دیگری مثل موکِت، پتو یا پرده هم برای دریافت خدمات دارید؟
                </p>
                <div className="cta-row">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setAskMore(false);
                      setSelectedCategory(null);
                      setManualStage(1);
                    }}
                  >
                    بله، مورد دیگری دارم
                  </button>
                  <button
                    type="button"
                    className="cta-primary"
                    onClick={() => {
                      setAskMore(false);
                      setManualStage(3);
                    }}
                  >
                    نه، برویم مرحله بعد
                  </button>
                </div>
              </section>
            </div>,
            modalHost,
          )
        : null}
    </>
  );
}
